import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import {
  RedoCheckbox,
  type CheckboxValue,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@redotech/redo-web/components/ui/accordion";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  getReturnRateSourceOptionLabel,
  getReturnRateSourceOptionTooltip,
  type AccountServiceLevelSelection,
  type ReturnRateSourceOption,
} from "../rate-source-selection-utils";
import { CarrierAvatar } from "./carrier-avatar";

interface SpecificMerchantCarrierAccountServiceAccordionProps {
  onAllServiceLevelsChange(selected: boolean): void;
  onServiceLevelToggle(serviceLevel: string, selected: boolean): void;
  option: ReturnRateSourceOption;
  selection: AccountServiceLevelSelection;
}

const accountTooltipEnterDelayMs = 500;

export function SpecificMerchantCarrierAccountServiceLevelAccordion({
  onAllServiceLevelsChange,
  onServiceLevelToggle,
  option,
  selection,
}: SpecificMerchantCarrierAccountServiceAccordionProps) {
  const { t } = useTranslation();
  const serviceLevelOptions = option.serviceLevelOptions ?? [];
  const accountCheckboxValue: CheckboxValue =
    selection.kind === "all"
      ? true
      : selection.kind === "some"
        ? "indeterminate"
        : false;
  const selectedCount =
    selection.kind === "all"
      ? serviceLevelOptions.length
      : selection.kind === "some"
        ? selection.serviceLevels.size
        : 0;
  const selectionSummary = `(${selectedCount}/${serviceLevelOptions.length})`;

  // Indeterminate -> select all, matching the inner All checkbox's native cycle.
  const handleAccountToggle = useHandler((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    onAllServiceLevelsChange(selection.kind !== "all");
  });
  const noop = useHandler(() => {});

  const tooltip = getReturnRateSourceOptionTooltip(t, option);
  const label = (
    <Text fontSize="xs">{getReturnRateSourceOptionLabel(t, option)}</Text>
  );

  return (
    <Accordion className="w-full" collapsible type="single">
      <AccordionItem className="border-b-0" value={option.id}>
        {/* Keep the account checkbox outside the trigger for accessibility. */}
        <Flex
          align="center"
          className="[&>h3]:min-w-0 [&>h3]:flex-1"
          gap="sm"
          w="full"
        >
          <Flex onClick={handleAccountToggle}>
            <RedoCheckbox
              ariaLabel={getReturnRateSourceOptionLabel(t, option)}
              setValue={noop}
              size="xs"
              value={accountCheckboxValue}
            />
          </Flex>
          <AccordionTrigger className="min-w-0 py-0 font-normal hover:no-underline">
            <Flex align="center" gap="sm" w="full">
              {option.logoCarrier && (
                <CarrierAvatar carrier={option.logoCarrier} />
              )}
              {tooltip ? (
                <Tooltip
                  arrow
                  enterDelay={accountTooltipEnterDelayMs}
                  enterNextDelay={accountTooltipEnterDelayMs}
                  placement="top"
                  title={tooltip}
                >
                  <Flex w="fit-content">{label}</Flex>
                </Tooltip>
              ) : (
                label
              )}
              <Flex align="center" gap="sm" grow={1} justify="flex-end">
                <Text fontSize="xs" textColor="secondary">
                  {selectionSummary}
                </Text>
              </Flex>
            </Flex>
          </AccordionTrigger>
        </Flex>
        <AccordionContent className="pb-0">
          <Flex dir="column" gap="sm" pb="sm" pl="3xl" pt="sm">
            <RedoCheckbox
              label={t("All")}
              setValue={(value) => {
                onAllServiceLevelsChange(value === true);
              }}
              size="xs"
              value={selection.kind === "all"}
            />
            {serviceLevelOptions.map((serviceLevelOption) => (
              <RedoCheckbox
                key={serviceLevelOption.serviceLevel}
                label={serviceLevelOption.label}
                setValue={(value) => {
                  onServiceLevelToggle(
                    serviceLevelOption.serviceLevel,
                    value === true,
                  );
                }}
                size="xs"
                value={
                  selection.kind === "all" ||
                  (selection.kind === "some" &&
                    selection.serviceLevels.has(
                      serviceLevelOption.serviceLevel,
                    ))
                }
              />
            ))}
          </Flex>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
